import { Circle, X as XIcon, Check, RefreshCw, Lock, AlertTriangle } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { fetchWithAuth } from '../Utils/fetchWithAuth';
import API_ENDPOINTS from '../Config/api';
import ClipLoader from 'react-spinners/ClipLoader';
import { FiAlertTriangle, FiRefreshCw, FiShield, FiActivity, FiClock, FiAlertCircle, FiZap, FiCrosshair } from 'react-icons/fi';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { useTranslation } from 'react-i18next';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, ArcElement,
  PointElement, LineElement, Title, Tooltip, Legend, Filler
);

// ===== SISTEMA PRIORITÀ UNIFICATO (copiato da Networking.jsx per coerenza) =====
function computePriority(severity, mlScore) {
  const sev = (severity || 'medium').toLowerCase();
  const mlPct = Math.round((mlScore || 0) * 100);
  const matrix = {
    critical: mlPct >= 65 ? 'critica' : mlPct >= 40 ? 'alta' : 'media',
    high:     mlPct >= 80 ? 'critica' : mlPct >= 65 ? 'alta' : mlPct >= 40 ? 'media' : 'bassa',
    medium:   mlPct >= 80 ? 'alta' : mlPct >= 65 ? 'media' : mlPct >= 40 ? 'bassa' : 'minima',
    low:      mlPct >= 80 ? 'media' : mlPct >= 65 ? 'bassa' : 'minima'
  };
  const level = matrix[sev] || matrix.medium;
  const configs = {
    critica: { level: 'critica', label: 'netSurvSection.priorityCritical', shortLabel: 'netSurvSection.shortCritical', icon: <Circle size={14} className="fill-red-500 text-red-500" />, color: 'text-red-700', bg: 'bg-red-50 border-red-200', badgeBg: 'bg-red-100', badgeText: 'text-red-800', dot: 'bg-red-500' },
    alta:    { level: 'alta', label: 'netSurvSection.priorityHigh', shortLabel: 'netSurvSection.shortHigh', icon: <Circle size={14} className="fill-orange-500 text-orange-500" />, color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200', badgeBg: 'bg-orange-100', badgeText: 'text-orange-800', dot: 'bg-orange-500' },
    media:   { level: 'media', label: 'netSurvSection.priorityMedium', shortLabel: 'netSurvSection.shortMedium', icon: <Circle size={14} className="fill-amber-500 text-amber-500" />, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', badgeBg: 'bg-amber-100', badgeText: 'text-amber-800', dot: 'bg-amber-500' },
    bassa:   { level: 'bassa', label: 'netSurvSection.priorityLow', shortLabel: 'netSurvSection.shortLow', icon: <Circle size={14} className="fill-blue-500 text-blue-500" />, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', badgeBg: 'bg-blue-100', badgeText: 'text-blue-800', dot: 'bg-blue-500' },
    minima:  { level: 'minima', label: 'netSurvSection.priorityMinimal', shortLabel: 'netSurvSection.shortMinimal', icon: <Circle size={14} className="fill-green-500 text-green-500" />, color: 'text-green-700', bg: 'bg-green-50 border-green-200', badgeBg: 'bg-green-100', badgeText: 'text-green-800', dot: 'bg-green-500' }
  };
  return configs[level];
}

function PriorityBadge({ severity, mlScore }) {
  const { t } = useTranslation();
  const p = computePriority(severity, mlScore);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${p.badgeBg} ${p.badgeText}`}>
      <span className={`w-2 h-2 rounded-full ${p.dot}`}></span>
      {t(p.shortLabel)}
    </span>
  );
}

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const config = {
    'open':            { bg: 'bg-red-100', text: 'text-red-800', label: t('netSurvSection.statusOpen') },
    'in-progress':     { bg: 'bg-blue-100', text: 'text-blue-800', label: t('netSurvSection.statusInProgress') },
    'confermato':      { bg: 'bg-orange-100', text: 'text-orange-800', label: t('netSurvSection.statusConfirmed') },
    'falso positivo':  { bg: 'bg-gray-100', text: 'text-gray-600', label: t('netSurvSection.statusFalsePositive') },
    'chiuso':          { bg: 'bg-green-100', text: 'text-green-800', label: t('netSurvSection.statusClosed') }
  };
  const s = config[(status || '').toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-600', label: status || '—' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

/**
 * Sezione Networking per la pagina CyberLog.
 * Mostra solo incidenti confermati + grafici settimanali/mensili/trimestrali.
 */
export default function NetworkingSurveillanceSection() {
  const { t } = useTranslation();

  const threatNames = {
    'c2_beacon': t('netSurvSection.threatC2'),
    'data_exfiltration': t('netSurvSection.threatExfiltration'),
    'ssh_bruteforce': t('netSurvSection.threatSSH'),
    'port_scan': t('netSurvSection.threatPortScan'),
    'dns_tunneling': t('netSurvSection.threatDNS'),
    'lateral_movement': t('netSurvSection.threatLateral'),
    'malware_communication': t('netSurvSection.threatMalware'),
    'syn_flood': t('netSurvSection.threatSynFlood'),
    'ddos': t('netSurvSection.threatDDoS'),
    'brute_force': t('netSurvSection.threatBruteForce'),
    'phishing': t('netSurvSection.threatPhishing'),
    'ransomware': t('netSurvSection.threatRansomware'),
    'unknown': t('netSurvSection.threatUnknown')
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [tenantData, setTenantData] = useState(null);
  const [incidentPage, setIncidentPage] = useState(1);
  const INCIDENTS_PER_PAGE = 5;
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [timeRange, setTimeRange] = useState('7d'); // '7d' | '30d' | '90d'
  const [topologyData, setTopologyData] = useState(null);

  const timeRangeLabels = {
    '7d': t('netSurvSection.weekly'),
    '30d': t('netSurvSection.monthly'),
    '90d': t('netSurvSection.quarterly'),
    '365d': t('netSurvSection.yearly')
  };

  const fetchData = async (range = timeRange) => {
    setLoading(true);
    setError(null);
    try {
      // 1. Get tenant
      const tenantsRes = await fetchWithAuth(API_ENDPOINTS.netflowTenants);
      if (!tenantsRes.ok) throw new Error(t('netSurvSection.cannotRetrieveTenants'));
      const tenantsData = await tenantsRes.json();
      const tenants = Array.isArray(tenantsData) ? tenantsData : tenantsData?.results || [];
      const activeTenant = tenants.find(t => t.status === 'ACTIVE') || tenants[0];
      
      if (!activeTenant) {
        setError('noTenant');
        setLoading(false);
        return;
      }
      setTenantData(activeTenant);

      // 2. Fetch incidents for selected time range
      const incidentsUrl = `${API_ENDPOINTS.netflowTenants}${activeTenant.id}/incidents/?time_range=${range}`;
      const incidentsRes = await fetchWithAuth(incidentsUrl);
      if (incidentsRes.ok) {
        const incData = await incidentsRes.json();
        const incidentsArray = Array.isArray(incData) ? incData : (incData?.incidents || []);
        
        const mapped = incidentsArray.map((inc) => {
          let normalizedStatus = (inc.status || 'OPEN').toUpperCase();
          if (normalizedStatus === 'APERTO' || normalizedStatus === 'OPEN') normalizedStatus = 'open';
          else if (normalizedStatus === 'IN CORSO' || normalizedStatus === 'IN_CORSO') normalizedStatus = 'in-progress';
          else if (normalizedStatus === 'CONFERMATO' || normalizedStatus === 'CONFIRMED') normalizedStatus = 'confermato';
          else if (normalizedStatus === 'FALSO POSITIVO' || normalizedStatus === 'FALSO_POSITIVO' || normalizedStatus === 'FALSE_POSITIVE') normalizedStatus = 'falso positivo';
          else if (normalizedStatus === 'CHIUSO' || normalizedStatus === 'CLOSED') normalizedStatus = 'chiuso';
          else normalizedStatus = normalizedStatus.toLowerCase();

          return {
            id: inc.formatted_id || inc.id,
            remote_id: inc.id,
            severity: (inc.severity || 'MEDIUM').toLowerCase(),
            status: normalizedStatus,
            threat_type: inc.threat_type || 'unknown',
            source_ip: inc.source_ip || inc.src_addr || null,
            dest_ip: inc.dest_ip || inc.destination_ip || inc.dst_addr || null,
            ml_score: parseFloat(inc.ml_score) || 0.5,
            opened_at: inc.created_at || inc.opened_at || new Date().toISOString(),
            closed_at: inc.closed_at || null,
            description: inc.description || null,
            comments: inc.comments || []
          };
        });
        setIncidents(mapped);
      } else {
        setIncidents([]);
      }

      // 3. Fetch topology data for device-type grouping
      try {
        const topoUrl = `${API_ENDPOINTS.netflowTenants}${activeTenant.id}/topology/?minutes=60`;
        const topoRes = await fetchWithAuth(topoUrl);
        if (topoRes.ok) {
          const topoData = await topoRes.json();
          if (topoData?.nodes?.length > 0) {
            setTopologyData(topoData);
          } else {
            setTopologyData(null);
          }
        }
      } catch (e) {
        console.warn('Topology fetch failed:', e);
        setTopologyData(null);
      }
    } catch (err) {
      console.error('NetworkingSurveillance fetch error:', err);
      setError(err.message || t('netSurvSection.dataLoadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(timeRange); }, [timeRange]);

  const handleTimeRangeChange = (newRange) => {
    setTimeRange(newRange);
    setIncidentPage(1);
  };

  // Solo incidenti confermati
  const confirmedIncidents = useMemo(() =>
    incidents.filter(inc => inc.status === 'confermato'),
    [incidents]
  );

  // ===== IP → Device Type mapping from topology =====
  const ipToDeviceType = useMemo(() => {
    const map = {};
    if (topologyData?.nodes) {
      topologyData.nodes.forEach(node => {
        const ip = node.ip || node.address;
        const dtype = (node.type || node.device_type || 'altro').toLowerCase();
        if (ip) map[ip] = dtype;
      });
    }
    return map;
  }, [topologyData]);

  const deviceTypeLabels = {
    router: t('netSurvSection.deviceRouter'),
    gateway: t('netSurvSection.deviceGateway'),
    switch: t('netSurvSection.deviceSwitch'),
    server: t('netSurvSection.deviceServer'),
    pc: t('netSurvSection.devicePC'),
    printer: t('netSurvSection.devicePrinter'),
    phone: t('netSurvSection.devicePhone'),
    ap: t('netSurvSection.deviceAP'),
    firewall: t('netSurvSection.deviceFirewall'),
    other: t('netSurvSection.deviceOther'),
    altro: t('netSurvSection.deviceOther'),
    unknown: t('netSurvSection.deviceUnknown')
  };

  // ===== Avg Resolution Time (segnalato → confermato) =====
  const avgResolutionTime = useMemo(() => {
    const times = confirmedIncidents
      .filter(inc => inc.closed_at && inc.opened_at)
      .map(inc => {
        const start = new Date(inc.opened_at).getTime();
        const end = new Date(inc.closed_at).getTime();
        return end > start ? end - start : null;
      })
      .filter(Boolean);

    if (times.length === 0) return null;
    const avgMs = times.reduce((a, b) => a + b, 0) / times.length;
    return avgMs;
  }, [confirmedIncidents]);

  const formatDuration = (ms) => {
    if (!ms || ms <= 0) return '—';
    const totalMinutes = Math.round(ms / 60000);
    if (totalMinutes < 60) return `${totalMinutes}m`;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours < 24) return `${hours}h ${minutes}m`;
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return `${days}g ${remHours}h`;
  };

  // ===== Group incidents by topology device type =====
  const topologyGroups = useMemo(() => {
    const groups = {};
    confirmedIncidents.forEach(inc => {
      const srcType = ipToDeviceType[inc.source_ip] || null;
      const dstType = ipToDeviceType[inc.dest_ip] || null;
      const deviceType = srcType || dstType || 'unknown';
      if (!groups[deviceType]) groups[deviceType] = [];
      groups[deviceType].push(inc);
    });
    return Object.entries(groups)
      .sort((a, b) => b[1].length - a[1].length)
      .map(([type, items]) => ({
        type,
        label: deviceTypeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1),
        count: items.length,
        critical: items.filter(i => (i.severity || '').toLowerCase() === 'critical').length,
        high: items.filter(i => (i.severity || '').toLowerCase() === 'high').length,
        medium: items.filter(i => (i.severity || '').toLowerCase() === 'medium').length,
        low: items.filter(i => (i.severity || '').toLowerCase() === 'low').length,
      }));
  }, [confirmedIncidents, ipToDeviceType]);

  // ===== Group incidents by threat type =====
  const threatGroups = useMemo(() => {
    const groups = {};
    confirmedIncidents.forEach(inc => {
      const tt = (inc.threat_type || 'unknown').toLowerCase();
      if (!groups[tt]) groups[tt] = [];
      groups[tt].push(inc);
    });
    return Object.entries(groups)
      .sort((a, b) => b[1].length - a[1].length)
      .map(([type, items]) => ({
        type,
        label: threatNames[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        count: items.length,
        critical: items.filter(i => (i.severity || '').toLowerCase() === 'critical').length,
        high: items.filter(i => (i.severity || '').toLowerCase() === 'high').length,
        medium: items.filter(i => (i.severity || '').toLowerCase() === 'medium').length,
        low: items.filter(i => (i.severity || '').toLowerCase() === 'low').length,
      }));
  }, [confirmedIncidents]);

  // ===== CHART DATA (dynamic based on timeRange) =====
  const chartData = useMemo(() => {
    const now = new Date();
    const buckets = [];

    if (timeRange === '7d') {
      // 7 giorni, un bucket per giorno
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        buckets.push({
          label: d.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' }),
          startKey: d.toISOString().split('T')[0],
          endKey: d.toISOString().split('T')[0],
          critical: 0, high: 0, medium: 0, low: 0, total: 0
        });
      }
    } else if (timeRange === '30d') {
      // 30 giorni, un bucket per settimana (4-5 settimane)
      for (let w = 3; w >= 0; w--) {
        const weekEnd = new Date(now);
        weekEnd.setDate(weekEnd.getDate() - (w * 7));
        const weekStart = new Date(weekEnd);
        weekStart.setDate(weekStart.getDate() - 6);
        buckets.push({
          label: `${weekStart.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} - ${weekEnd.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}`,
          startKey: weekStart.toISOString().split('T')[0],
          endKey: weekEnd.toISOString().split('T')[0],
          critical: 0, high: 0, medium: 0, low: 0, total: 0
        });
      }
    } else if (timeRange === '90d') {
      // 90 giorni, un bucket per mese (3 mesi)
      for (let m = 2; m >= 0; m--) {
        const monthStart = new Date(now.getFullYear(), now.getMonth() - m, 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() - m + 1, 0);
        buckets.push({
          label: monthStart.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }),
          startKey: monthStart.toISOString().split('T')[0],
          endKey: monthEnd.toISOString().split('T')[0],
          critical: 0, high: 0, medium: 0, low: 0, total: 0
        });
      }
    } else {
      // 365 giorni, un bucket per mese (12 mesi)
      for (let m = 11; m >= 0; m--) {
        const monthStart = new Date(now.getFullYear(), now.getMonth() - m, 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() - m + 1, 0);
        buckets.push({
          label: monthStart.toLocaleDateString('it-IT', { month: 'short', year: '2-digit' }),
          startKey: monthStart.toISOString().split('T')[0],
          endKey: monthEnd.toISOString().split('T')[0],
          critical: 0, high: 0, medium: 0, low: 0, total: 0
        });
      }
    }

    confirmedIncidents.forEach(inc => {
      const incDate = new Date(inc.opened_at).toISOString().split('T')[0];
      const bucket = buckets.find(b => incDate >= b.startKey && incDate <= b.endKey);
      if (bucket) {
        bucket.total++;
        const sev = (inc.severity || 'medium').toLowerCase();
        if (sev === 'critical') bucket.critical++;
        else if (sev === 'high') bucket.high++;
        else if (sev === 'medium') bucket.medium++;
        else bucket.low++;
      }
    });

    return buckets;
  }, [confirmedIncidents, timeRange]);

  // Severity distribution for doughnut
  const severityDistribution = useMemo(() => {
    const dist = { critical: 0, high: 0, medium: 0, low: 0 };
    confirmedIncidents.forEach(inc => {
      const sev = (inc.severity || 'medium').toLowerCase();
      if (dist[sev] !== undefined) dist[sev]++;
      else dist.medium++;
    });
    return dist;
  }, [confirmedIncidents]);

  // KPIs
  const kpis = useMemo(() => {
    const criticalHigh = confirmedIncidents.filter(i => {
      const p = computePriority(i.severity, i.ml_score);
      return p.level === 'critica' || p.level === 'alta';
    }).length;
    const mediumLow = confirmedIncidents.filter(i => {
      const p = computePriority(i.severity, i.ml_score);
      return p.level === 'media' || p.level === 'bassa' || p.level === 'minima';
    }).length;
    const analyzed = incidents.length;
    return { total: confirmedIncidents.length, criticalHigh, mediumLow, analyzed, avgResolution: avgResolutionTime };
  }, [confirmedIncidents, incidents, avgResolutionTime]);

  // Paginated incidents (only confirmed)
  const totalPages = Math.max(1, Math.ceil(confirmedIncidents.length / INCIDENTS_PER_PAGE));
  const paginatedIncidents = confirmedIncidents.slice(
    (incidentPage - 1) * INCIDENTS_PER_PAGE,
    incidentPage * INCIDENTS_PER_PAGE
  );

  // Status update handler
  const handleUpdateStatus = async (incidentId, newStatus) => {
    if (!tenantData) return;
    setUpdatingStatus(true);
    const incident = incidents.find(inc => inc.id === incidentId || String(inc.remote_id) === String(incidentId));
    const numericId = incident?.remote_id ? Number(incident.remote_id) : Number(incidentId);
    
    try {
      const url = API_ENDPOINTS.updateNetflowIncidentStatus(tenantData.id, numericId);
      const res = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        const normalizeLocal = (s) => {
          const upper = (s || '').toUpperCase();
          if (upper === 'FALSO POSITIVO' || upper === 'FALSO_POSITIVO') return 'falso positivo';
          if (upper === 'CONFERMATO') return 'confermato';
          if (upper === 'CHIUSO') return 'chiuso';
          if (upper === 'IN CORSO' || upper === 'IN_CORSO') return 'in-progress';
          if (upper === 'APERTO') return 'open';
          return s.toLowerCase();
        };
        const localStatus = normalizeLocal(newStatus);
        setIncidents(prev => prev.map(inc =>
          (inc.id === incidentId || String(inc.remote_id) === String(numericId))
            ? { ...inc, status: localStatus }
            : inc
        ));
        setSelectedIncident(null);
      } else {
        alert(t('netSurvSection.errorSavingStatus'));
      }
    } catch {
      alert(t('netSurvSection.networkErrorSaving'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Comment handler
  const handleAddComment = async (incidentId) => {
    if (!tenantData || !commentText.trim()) return;
    const incident = incidents.find(inc => inc.id === incidentId || String(inc.remote_id) === String(incidentId));
    const numericId = incident?.remote_id ? Number(incident.remote_id) : Number(incidentId);
    
    try {
      const url = API_ENDPOINTS.addNetflowIncidentComment(tenantData.id, numericId);
      const res = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: commentText.trim() })
      });
      if (res.ok) {
        const newComment = { text: commentText.trim(), user: 'Utente', timestamp: new Date().toISOString() };
        setIncidents(prev => prev.map(inc =>
          (inc.id === incidentId || String(inc.remote_id) === String(numericId))
            ? { ...inc, comments: [...(inc.comments || []), newComment] }
            : inc
        ));
        if (selectedIncident && (selectedIncident.id === incidentId || String(selectedIncident.remote_id) === String(numericId))) {
          setSelectedIncident(prev => ({ ...prev, comments: [...(prev.comments || []), newComment] }));
        }
        setCommentText('');
      }
    } catch {
      alert(t('netSurvSection.errorSendingComment'));
    }
  };

  // ===== CHART CONFIGS =====
  const periodLabel = timeRangeLabels[timeRange] || t('netSurvSection.weekly');

  const barChartData = {
    labels: chartData.map(d => d.label),
    datasets: [
      { label: t('netSurvSection.chartCritical'), data: chartData.map(d => d.critical), backgroundColor: '#ef4444', borderRadius: 4 },
      { label: t('netSurvSection.chartHigh'), data: chartData.map(d => d.high), backgroundColor: '#f97316', borderRadius: 4 },
      { label: t('netSurvSection.chartMedium'), data: chartData.map(d => d.medium), backgroundColor: '#eab308', borderRadius: 4 },
      { label: t('netSurvSection.chartLow'), data: chartData.map(d => d.low), backgroundColor: '#3b82f6', borderRadius: 4 },
    ]
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, pointStyle: 'circle', padding: 16, font: { size: 11 } } },
      title: { display: true, text: `${t('netSurvSection.confirmedIncidentsChart')} — ${periodLabel}`, font: { size: 13, weight: '600' }, color: '#374151', padding: { bottom: 12 } }
    },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1, precision: 0 }, grid: { color: '#f3f4f6' } }
    }
  };

  const doughnutData = {
    labels: [t('netSurvSection.chartCritical'), t('netSurvSection.chartHigh'), t('netSurvSection.chartMedium'), t('netSurvSection.chartLow')],
    datasets: [{
      data: [severityDistribution.critical, severityDistribution.high, severityDistribution.medium, severityDistribution.low],
      backgroundColor: ['#ef4444', '#f97316', '#eab308', '#3b82f6'],
      borderWidth: 2,
      borderColor: '#ffffff'
    }]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, pointStyle: 'circle', padding: 12, font: { size: 11 } } },
      title: { display: true, text: t('netSurvSection.severityDistribution'), font: { size: 13, weight: '600' }, color: '#374151', padding: { bottom: 8 } }
    },
    cutout: '60%'
  };

  const lineChartData = {
    labels: chartData.map(d => d.label),
    datasets: [{
      label: t('netSurvSection.confirmedIncidentsChart'),
      data: chartData.map(d => d.total),
      borderColor: '#6366f1',
      backgroundColor: 'rgba(99, 102, 241, 0.1)',
      fill: true,
      tension: 0.3,
      pointBackgroundColor: chartData.map((_, i) => i === chartData.length - 1 ? '#4f46e5' : '#6366f1'),
      pointRadius: chartData.map((_, i) => i === chartData.length - 1 ? 6 : 3),
      pointBorderWidth: chartData.map((_, i) => i === chartData.length - 1 ? 3 : 1),
      pointBorderColor: chartData.map((_, i) => i === chartData.length - 1 ? '#ffffff' : '#6366f1'),
    }]
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: true, text: `${t('netSurvSection.confirmedIncidentsTrend')} — ${periodLabel}`, font: { size: 13, weight: '600' }, color: '#374151', padding: { bottom: 12 } }
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { stepSize: 1, precision: 0 }, grid: { color: '#f3f4f6' } }
    }
  };

  // ===== LOADING / ERROR STATES =====
  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6 h-full w-full flex items-center justify-center">
        <ClipLoader size={40} />
      </div>
    );
  }

  if (error === 'noTenant') {
    return (
      <div className="bg-white border rounded-lg p-6 h-full w-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <FiShield className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p className="font-medium">{t('netSurvSection.serviceNotActive')}</p>
          <p className="text-sm mt-1">{t('netSurvSection.noTenantConfigured')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border rounded-lg p-6 h-full w-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <FiAlertTriangle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>{error}</p>
          <button onClick={fetchData} className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mx-auto">
            <FiRefreshCw className="w-4 h-4" /> {t('netSurvSection.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full overflow-y-auto" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
      
      {/* Time Range Selector + KPI Cards */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {Object.entries(timeRangeLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => handleTimeRangeChange(key)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                timeRange === key
                  ? 'bg-yellow-400 text-black border-yellow-500'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <button onClick={() => fetchData(timeRange)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
          <FiRefreshCw className="w-3 h-3" /> {t('netSurvSection.refresh')}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-3 border border-orange-200">
          <div className="flex items-center gap-2 mb-1">
            <FiShield className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700">{t('netSurvSection.totalConfirmed')}</span>
          </div>
          <div className="text-2xl font-bold text-orange-700">{kpis.total}</div>
          <div className="text-xs text-orange-500">{t('netSurvSection.inSelectedPeriod')}</div>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-3 border border-red-200">
          <div className="flex items-center gap-2 mb-1">
            <FiAlertCircle className="w-4 h-4 text-red-600" />
            <span className="text-xs font-semibold text-red-700">{t('netSurvSection.criticalHigh')}</span>
          </div>
          <div className="text-2xl font-bold text-red-600">{kpis.criticalHigh}</div>
          <div className="text-xs text-red-500">{t('netSurvSection.highPriority')}</div>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 border border-amber-200">
          <div className="flex items-center gap-2 mb-1">
            <FiActivity className="w-4 h-4 text-amber-600" />
            <span className="text-xs font-semibold text-amber-700">{t('netSurvSection.mediumLow')}</span>
          </div>
          <div className="text-2xl font-bold text-amber-600">{kpis.mediumLow}</div>
          <div className="text-xs text-amber-500">{t('netSurvSection.lowerPriority')}</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <FiClock className="w-4 h-4 text-green-600" />
            <span className="text-xs font-semibold text-green-700">{t('netSurvSection.analyzed')}</span>
          </div>
          <div className="text-2xl font-bold text-green-600">{kpis.analyzed}</div>
          <div className="text-xs text-green-500">{t('netSurvSection.suspiciousEvents')}</div>
        </div>
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-3 border border-indigo-200">
          <div className="flex items-center gap-2 mb-1">
            <FiZap className="w-4 h-4 text-indigo-600" />
            <span className="text-xs font-semibold text-indigo-700">{t('netSurvSection.avgTime')}</span>
          </div>
          <div className="text-2xl font-bold text-indigo-600">{kpis.avgResolution ? formatDuration(kpis.avgResolution) : '—'}</div>
          <div className="text-xs text-indigo-500">{t('netSurvSection.reportedToConfirmed')}</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Bar Chart — Incidenti per giorno */}
        <div className="bg-white rounded-lg p-4 border border-gray-200 lg:col-span-1">
          <div style={{ height: 220 }}>
            <Bar data={barChartData} options={barChartOptions} />
          </div>
        </div>

        {/* Line Chart — Trend settimanale */}
        <div className="bg-white rounded-lg p-4 border border-gray-200 lg:col-span-1">
          <div style={{ height: 220 }}>
            <Line data={lineChartData} options={lineChartOptions} />
          </div>
        </div>

        {/* Doughnut — Distribuzione severity */}
        <div className="bg-white rounded-lg p-4 border border-gray-200 lg:col-span-1">
          <div style={{ height: 220 }}>
            {confirmedIncidents.length > 0 ? (
              <Doughnut data={doughnutData} options={doughnutOptions} />
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                {t('netSurvSection.noChartData')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Threat Type Grouping */}
      {threatGroups.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FiCrosshair className="w-4 h-4 text-gray-700" /> {t('netSurvSection.incidentsByThreatType')}
            </h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Horizontal Bars */}
            <div className="p-4 border-r border-gray-100">
              <div className="space-y-2">
                {threatGroups.map(g => {
                  const maxCount = threatGroups[0]?.count || 1;
                  const pct = Math.round((g.count / maxCount) * 100);
                  return (
                    <div key={g.type}>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="font-medium text-gray-700">{g.label}</span>
                        <span className="font-semibold text-gray-900">{g.count}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div className="h-full rounded-full flex overflow-hidden" style={{ width: `${pct}%` }}>
                          {g.critical > 0 && <div className="bg-red-500 h-full" style={{ width: `${(g.critical / g.count) * 100}%` }} />}
                          {g.high > 0 && <div className="bg-orange-500 h-full" style={{ width: `${(g.high / g.count) * 100}%` }} />}
                          {g.medium > 0 && <div className="bg-amber-400 h-full" style={{ width: `${(g.medium / g.count) * 100}%` }} />}
                          {g.low > 0 && <div className="bg-blue-400 h-full" style={{ width: `${(g.low / g.count) * 100}%` }} />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>{t('netSurvSection.chartCritical')}</span>
                <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-orange-500 inline-block"></span>{t('netSurvSection.chartHigh')}</span>
                <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block"></span>{t('netSurvSection.chartMedium')}</span>
                <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block"></span>{t('netSurvSection.chartLow')}</span>
              </div>
            </div>

            {/* Summary Table */}
            <div className="p-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-100">
                    <th className="text-left py-1.5 font-semibold">{t('netSurvSection.threat')}</th>
                    <th className="text-center py-1.5 font-semibold">{t('netSurvSection.total')}</th>
                    <th className="text-center py-1.5 font-semibold"><Circle size={12} className="fill-red-500 text-red-500 inline" /></th>
                    <th className="text-center py-1.5 font-semibold"><Circle size={12} className="fill-orange-500 text-orange-500 inline" /></th>
                    <th className="text-center py-1.5 font-semibold"><Circle size={12} className="fill-amber-500 text-amber-500 inline" /></th>
                    <th className="text-center py-1.5 font-semibold"><Circle size={12} className="fill-blue-500 text-blue-500 inline" /></th>
                  </tr>
                </thead>
                <tbody>
                  {threatGroups.map(g => (
                    <tr key={g.type} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-1.5 font-medium text-gray-800">{g.label}</td>
                      <td className="py-1.5 text-center font-semibold text-gray-900">{g.count}</td>
                      <td className="py-1.5 text-center text-red-600">{g.critical || '—'}</td>
                      <td className="py-1.5 text-center text-orange-600">{g.high || '—'}</td>
                      <td className="py-1.5 text-center text-amber-600">{g.medium || '—'}</td>
                      <td className="py-1.5 text-center text-blue-600">{g.low || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Incident Detail Modal */}
      {selectedIncident && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedIncident(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selectedIncident.id}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {threatNames[(selectedIncident.threat_type || '').toLowerCase()] || selectedIncident.threat_type}
                  </p>
                </div>
                <button onClick={() => setSelectedIncident(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold"><XIcon size={12} className="inline" /></button>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <PriorityBadge severity={selectedIncident.severity} mlScore={selectedIncident.ml_score} />
                <StatusBadge status={selectedIncident.status} />
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('netSurvSection.details')}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">{t('netSurvSection.severity')}:</span> <span className="font-medium">{selectedIncident.severity}</span></div>
                  <div><span className="text-gray-500">{t('netSurvSection.mlScore')}:</span> <span className="font-medium">{(selectedIncident.ml_score * 100).toFixed(0)}%</span></div>
                  <div><span className="text-gray-500">{t('netSurvSection.sourceIP')}:</span> <span className="font-mono text-xs">{selectedIncident.source_ip || '—'}</span></div>
                  <div><span className="text-gray-500">{t('netSurvSection.destIP')}:</span> <span className="font-mono text-xs">{selectedIncident.dest_ip || '—'}</span></div>
                  <div className="col-span-2"><span className="text-gray-500">{t('netSurvSection.date')}:</span> <span>{new Date(selectedIncident.opened_at).toLocaleString('it-IT')}</span></div>
                  {(ipToDeviceType[selectedIncident.source_ip] || ipToDeviceType[selectedIncident.dest_ip]) && (
                    <div className="col-span-2"><span className="text-gray-500">{t('netSurvSection.device')}:</span> <span className="font-medium">{deviceTypeLabels[ipToDeviceType[selectedIncident.source_ip] || ipToDeviceType[selectedIncident.dest_ip]] || t('netSurvSection.deviceOther')}</span></div>
                  )}
                  {selectedIncident.closed_at && selectedIncident.opened_at && (
                    <div className="col-span-2"><span className="text-gray-500">{t('netSurvSection.resolutionTime')}:</span> <span className="font-medium">{formatDuration(new Date(selectedIncident.closed_at).getTime() - new Date(selectedIncident.opened_at).getTime())}</span></div>
                  )}
                </div>
                {selectedIncident.description && (
                  <p className="mt-2 text-sm text-gray-600 bg-gray-50 rounded p-2">{selectedIncident.description}</p>
                )}
              </div>

              {/* Status Actions */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('netSurvSection.changeStatus')}</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedIncident.status !== 'confermato' && (
                    <button
                      disabled={updatingStatus}
                      onClick={() => handleUpdateStatus(selectedIncident.id, 'CONFERMATO')}
                      className="px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-lg hover:bg-orange-200 border border-orange-200 disabled:opacity-50"
                    >
                      <Check size={12} className="inline" /> {t('netSurvSection.confirmThreat')}
                    </button>
                  )}
                  {selectedIncident.status !== 'in-progress' && (
                    <button
                      disabled={updatingStatus}
                      onClick={() => handleUpdateStatus(selectedIncident.id, 'IN CORSO')}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 border border-blue-200 disabled:opacity-50"
                    >
                      <RefreshCw size={12} className="inline" /> {t('netSurvSection.inProgress')}
                    </button>
                  )}
                  {selectedIncident.status !== 'chiuso' && (
                    <button
                      disabled={updatingStatus}
                      onClick={() => handleUpdateStatus(selectedIncident.id, 'CHIUSO')}
                      className="px-3 py-1.5 text-xs font-medium bg-green-100 text-green-800 rounded-lg hover:bg-green-200 border border-green-200 disabled:opacity-50"
                    >
                      <Lock size={12} className="inline" /> {t('netSurvSection.close')}
                    </button>
                  )}
                  {selectedIncident.status !== 'falso positivo' && (
                    <button
                      disabled={updatingStatus}
                      onClick={() => handleUpdateStatus(selectedIncident.id, 'FALSO POSITIVO')}
                      className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 border border-gray-200 disabled:opacity-50"
                    >
                      <XIcon size={12} className="inline" /> {t('netSurvSection.falsePositive')}
                    </button>
                  )}
                </div>
              </div>

              {/* Comments */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('netSurvSection.comments')} ({(selectedIncident.comments || []).length})</h4>
                {(selectedIncident.comments || []).length > 0 && (
                  <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
                    {selectedIncident.comments.map((c, i) => (
                      <div key={i} className="bg-gray-50 rounded p-2 text-sm">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span className="font-medium text-gray-600">{c.user || 'Utente'}</span>
                          <span>{c.timestamp ? new Date(c.timestamp).toLocaleString('it-IT') : ''}</span>
                        </div>
                        <p className="text-gray-700">{c.text}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder={t('netSurvSection.addComment')}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddComment(selectedIncident.id)}
                  />
                  <button
                    onClick={() => handleAddComment(selectedIncident.id)}
                    disabled={!commentText.trim()}
                    className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {t('netSurvSection.send')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
